import React from "react";
import { GridRowClassNameParams, GridRowEditStartReasons, GridRowId, GridRowParams } from "@mui/x-data-grid-pro";
import {
	DataGridValidationContextProvider,
	DataGridValidationErrors,
} from "src/components/common/dataGrid/crud/validation/DataGridValidationContextProvider.tsx";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dataGridRowClassNames } from "src/components/common/dataGrid/styles/dataGridClassNames.ts";
import { GridApiPro } from "@mui/x-data-grid-pro";
import {
	CrudActionButtonDefinitionWithShortcuts,
	CrudActionButtonDefinition,
	removeActionShortcuts,
} from "src/components/common/crud/crudActionButtonDefinition.ts";
import { CrudActionButtons } from "../../crud/CrudActionButtons";
import { useForwardedRef } from "src/utils/useForwardedRef.ts";
import { GridRowIdGetter, GridRowModelUpdate } from "@mui/x-data-grid-pro";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";
import { isNotNullOrEmptyObject } from "src/utils/objectUtils.ts";
import { flattenAavoDataGridColumns } from "src/components/common/dataGrid/utils.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { confirmUnsavedChangesWillBeLost } from "src/components/common/dialogs/confirmDialog/confirmDialogUtils.ts";
import {
	hideRowContextMenuForNewRows,
	InlineCrudGridRowData,
	isNewRow,
	NewRowData,
	resolveCustomUiActionObjectIdForSavedRows,
	useInlineCrudGridRows,
} from "src/components/common/dataGrid/crud/useInlineCrudGridRows.ts";
import { useInlineCrudGridEditing } from "src/components/common/dataGrid/crud/useInlineCrudGridEditing.ts";
import { DataGridRowUpdateHandler } from "src/components/common/dataGrid/gridModel/dataGridRowUpdates.ts";
import { useCrudGridRemoveAction } from "src/components/common/dataGrid/crud/useCrudGridRemoveAction.ts";
import { useInlineCrudGridChanges } from "src/components/common/dataGrid/crud/useInlineCrudGridChanges.ts";
import i18n from "i18next";

export interface InlineCrudDataGridProps<RowData extends object> extends Omit<AavoDataGridProps<RowData>, "getRowId"> {
	refreshData?: () => Promise<unknown>;
	add?: CrudActionButtonDefinitionWithShortcuts<void, Partial<RowData>>;
	save?: CrudActionButtonDefinitionWithShortcuts<
		{
			items: RowData[];
		},
		RowData[]
	>;
	remove?: CrudActionButtonDefinitionWithShortcuts<
		{
			row: RowData;
			items: RowData[];
		},
		unknown
	>;
	getRowId: GridRowIdGetter<RowData>;
	inlineCrudGridApiRef?: React.RefObject<InlineCrudDataGridApi | null>;
	dataDirtyStateChanged?: DataDirtyStateChangeHandler;
	updateRows: DataGridRowUpdateHandler;
}

export interface InlineCrudDataGridApi {
	updateRows: (updates: GridRowModelUpdate[]) => void;
}

export const InlineCrudDataGrid = <RowData extends object>({
	refreshData: refreshDataProp,
	getRowId: getRowIdProp,
	add: addProp,
	save: saveProp,
	remove: removeProp,
	actionBarComponents,
	apiRef: forwardedApiRef,
	selectedRows: selectedRowsProp,
	onRowSelectionChanged: onRowSelectionChangedProp,
	rowCount,
	editMode = "row",
	onCellEditStart,
	onCellEditStop,
	onRowEditStart,
	onRowEditStop,
	onRowModesModelChange,
	getRowClassNames: getRowClassNamesProp,
	inlineCrudGridApiRef,
	onRowDoubleClick,
	columns,
	dataDirtyStateChanged,
	rows,
	updateRows,
	processRowUpdate,
	rowContextMenuComponents,
	customFunctionUiActionObjectId,
	...other
}: InlineCrudDataGridProps<RowData>) => {
	const add = removeActionShortcuts(addProp);
	const save = removeActionShortcuts(saveProp);
	const remove = removeActionShortcuts(removeProp);

	const showConfirmDialog = useConfirmDialog();
	const gridApiRef = useForwardedRef(forwardedApiRef);

	const [selectedRowIds, setSelectedRowIds] = useMaybeControlledState<GridRowId[]>({
		controlledValue: selectedRowsProp,
		onChange: onRowSelectionChangedProp,
		defaultValue: [],
	});

	const [validationErrors, setValidationErrors] = React.useState<DataGridValidationErrors>({});

	const getGridApi = React.useCallback((): GridApiPro => {
		const current = gridApiRef.current;
		if (isNotNullOrEmptyObject(current)) {
			return current;
		} else {
			const error = "GridApiRef has no value";
			console.error(error);
			throw new Error(error);
		}
	}, [gridApiRef]);

	const getRowId = wrapGetRowId();

	const localRowState = useInlineCrudGridRows({
		rows,
		getRowId,
		updateRows,
	});

	const editing = useInlineCrudGridEditing({
		editMode,
		rows: localRowState.localRows,
		gridApiRef: gridApiRef as React.RefObject<GridApiPro | null>,
		getGridApi,
	});

	const changes = useInlineCrudGridChanges({
		changedRowIds: localRowState.changedRowIds,
		getGridApi,
		getRowId,
		localRows: localRowState.localRows,
		processRowUpdate,
		rowUnderEdit: editing.rowUnderEdit,
		updateLocalRows: localRowState.updateLocalRows,
	});

	const refreshData =
		refreshDataProp == null ? undefined : (
			async (refreshParams?: { warnIfDirty?: boolean }) => {
				const { warnIfDirty = true } = refreshParams ?? {};
				if (warnIfDirty && localRowState.isDirty) {
					const confirmed = await confirmUnsavedChangesWillBeLost(showConfirmDialog);
					if (!confirmed) return;
				}
				await refreshDataProp();
				changes.resetChanges();
				localRowState.resetDirtyState();
				editing.resetEditing();
				setValidationErrors({});
			}
		);

	const rowCountWithDraftRows = rowCount === undefined ? undefined : rowCount + localRowState.draftRowIds.length;

	if (inlineCrudGridApiRef != null)
		inlineCrudGridApiRef.current = {
			updateRows: (updates: GridRowModelUpdate[]) => {
				const gridApi = getGridApi();
				gridApi.updateRows(updates);
				localRowState.updateRows(updates);
			},
		};

	const editingEnabled = add.type === "enabled" || save.type === "enabled";
	const mappedColumns =
		editingEnabled ? columns : (
			flattenAavoDataGridColumns(columns).map((column) => ({
				...column,
				editable: false,
			}))
	);
	const removeAction = useCrudGridRemoveAction({
		remove,
		selectedRowIds,
		getGridApi,
		getRows: () => localRowState.localRows,
		getRowId,
		shouldCallRemoveAction: isPersistedRow,
		onRowsRemoved: async ({ selectedRows, removedByActionRows, selectedRowIds }) => {
			if (removedByActionRows.length > 0) await refreshData?.();

			const localRowDeleteUpdates = localRowState.deleteRows(selectedRows);
			getGridApi().updateRows(localRowDeleteUpdates);
			clearDeletedRowsFromState(selectedRowIds);
		},
		clearSelection: () => setSelectedRowIds([]),
	});
	return (
		<DataGridValidationContextProvider setErrors={setValidationErrors}>
			<AavoDataGrid
				rowCount={rowCountWithDraftRows}
				apiRef={gridApiRef}
				columns={mappedColumns}
				getRowId={getRowId}
				editMode={editMode}
				refreshData={
					refreshData == null ? undefined : (
						async () => {
							await onRefresh();
						}
					)
				}
				actionBarComponents={
					<>
						<CrudActionButtons
							add={getAddAction()}
							save={getSaveAction()}
							remove={removeAction}
							edit={{ type: "hidden" }}
						/>
						{actionBarComponents}
					</>
				}
				onCellEditStart={(params, event, details) => {
					onCellEditStart?.(params, event, details);
					handleRowEditStarted(getRowId(params.row), isNewRow(params.row));
					dataDirtyStateChanged?.({ isDirty: true });
				}}
				onCellEditStop={(params, event, details) => {
					onCellEditStop?.(params, event, details);
					editing.stopEdit();
				}}
				onRowEditStart={(params, event, details) => {
					onRowEditStart?.(params, event, details);
					handlePrintableKeyRowEditStart(params, event);
					handleRowEditStarted(getRowId(params.row), isNewRow(params.row));
					dataDirtyStateChanged?.({ isDirty: true });
				}}
				onRowEditStop={(params, event, details) => {
					onRowEditStop?.(params, event, details);
					editing.stopEdit();
				}}
				getRowClassNames={(params) => getRowClassNames(params)}
				selectedRows={selectedRowIds}
				onRowSelectionChanged={setSelectedRowIds}
				onRowDoubleClick={async (params: GridRowParams<RowData>, event, details) => {
					if (onRowDoubleClick != null) {
						onRowDoubleClick?.(params, event, details);
					}
				}}
				processRowUpdate={changes.handleProcessRowUpdate}
				rowModesModel={editing.rowModesModel}
				onRowModesModelChange={(model, details) => {
					editing.setRowModesModel(model);
					onRowModesModelChange?.(model, details);
				}}
				rows={localRowState.localRows as RowData[]}
				rowContextMenuComponents={hideRowContextMenuForNewRows(rowContextMenuComponents)}
				customFunctionUiActionObjectId={resolveCustomUiActionObjectIdForSavedRows(
					getRowIdProp,
					customFunctionUiActionObjectId,
				)}
				{...other}
			/>
		</DataGridValidationContextProvider>
	);

	function wrapGetRowId() {
		return (row: RowData | NewRowData<RowData>) => {
			if (isNewRow(row)) {
				return row.__generated_id;
			} else {
				return getRowIdProp(row);
			}
		};
	}

	function clearDeletedRowsFromState(deletedRowIds: GridRowId[]) {
		changes.clearChangedRowsByIds(deletedRowIds);
		localRowState.clearDeletedRows(deletedRowIds);
		clearDeletedRowsFromValidationAndEditing(deletedRowIds);
	}

	function clearDeletedRowsFromValidationAndEditing(deletedRowIds: GridRowId[]) {
		setValidationErrors((prev) => {
			const next = { ...prev };
			deletedRowIds.forEach((rowId) => {
				delete next[String(rowId)];
			});
			return next;
		});
		editing.clearDeletedRows(deletedRowIds);
	}

	async function onRefresh() {
		const gridApi = getGridApi();
		// Bug in MUIGridPro requires that editing of unsaved rows is stopped manually before refresh.
		localRowState.draftRowIds
			.filter((rowId) => gridApi.getRowMode(rowId) === "edit")
			.forEach((rowId) => {
				gridApi.stopRowEditMode({
					id: rowId,
					ignoreModifications: true,
				});
		});
		await refreshData?.();
	}

	function addNewLocalUnsavedRow(newRow: Partial<RowData>) {
		const newRowGeneratedId = localRowState.addLocalRow(newRow);
		editing.requestStartNewRowEdit(newRowGeneratedId);
	}

	function getAddAction(): CrudActionButtonDefinition {
		if (add?.type !== "enabled") return { type: "hidden" };

		return {
			type: add.type,
			action: async () => {
				const newRow = await add.action();
				if (newRow !== "interrupt") {
					addNewLocalUnsavedRow(newRow);
				}
			},
		};
	}

	function getSaveAction(): CrudActionButtonDefinition {
		if (save?.type !== "enabled") return save ?? { type: "hidden" };

		const hasAnyValidationErrors = Object.values(validationErrors).some((rowErrors) =>
			Object.values(rowErrors).some((fieldError) => fieldError !== undefined),
		);

		if (hasAnyValidationErrors) {
			return {
				type: "disabled",
				tooltip: i18n.t("fix_errors_before_saving"),
			};
		}

		if (
			localRowState.draftRowIds.length === 0 &&
			localRowState.editedRowIds.length === 0 &&
			editing.rowUnderEdit === undefined
		) {
			return {
				type: "disabled",
				tooltip: i18n.t("no_unsaved_or_edited_rows"),
			};
		}

		return {
			type: save.type,
			action: async () => {
				await changes.waitForEditedRowToCommit();
				const changedRows = changes.getChangedRows();
				const savedRows = await save.action({ items: changedRows });
				if (savedRows === "interrupt") return "interrupt";
				const { deletedDraftRowIds, rowUpdates } = localRowState.replaceSavedRows(changedRows, savedRows);
				getGridApi().updateRows(rowUpdates);
				changes.clearChangedRows(changedRows);
				clearDeletedRowsFromValidationAndEditing(deletedDraftRowIds);
				dataDirtyStateChanged?.({ isDirty: false });
				await refreshData?.({ warnIfDirty: false });
			},
		};
	}

	function handleRowEditStarted(rowId: GridRowId, isNewRow: boolean) {
		editing.handleEditStarted(rowId);
		if (!isNewRow) {
			localRowState.markRowsEdited([rowId]);
		}
	}

	function handlePrintableKeyRowEditStart(
		params: Parameters<NonNullable<AavoDataGridProps<RowData>["onRowEditStart"]>>[0],
		event: Parameters<NonNullable<AavoDataGridProps<RowData>["onRowEditStart"]>>[1],
	) {
		if (event.defaultMuiPrevented) return;
		if (editMode !== "row") return;
		if (params.reason !== GridRowEditStartReasons.printableKeyDown) return;
		if (params.field == null || params.key == null) return;

		// MUI seeds controlled row edit with the printable key, but the original key event can still reach the
		// focused editor. Cancel the default start handling and native input so the first character is not duplicated.
		event.defaultMuiPrevented = true;
		event.preventDefault?.();
		getGridApi().startRowEditMode({
			id: params.id,
			fieldToFocus: params.field,
			initialValue: params.key,
		});
	}

	function isPersistedRow(row: InlineCrudGridRowData<RowData>): row is RowData & InlineCrudGridRowData<RowData> {
		return !isNewRow(row);
	}

	function getRowClassNames(params: GridRowClassNameParams): string[] {
		const ret = [];

		const fromProp = getRowClassNamesProp?.(params) ?? [];
		if (typeof fromProp === "string") ret.push(fromProp);
		else ret.push(...fromProp);

		if (params.row.isNewRow) {
			ret.push(dataGridRowClassNames.unsavedNew);
		} else if (localRowState.editedRowIds.includes(params.id)) {
			ret.push(dataGridRowClassNames.edited);
		}
		return ret;
	}
};
